import type { Terms } from '@/lib/useTerminology'
import {
  PROJECT_POSITION_STATUS_TEXT,
  PROJECT_POSITION_STATUS_TEXT_BIS_2026_09,
  PROJECT_STATUS_TEXT_BIS_2026_09,
  type ProjectStatus,
} from '@symphenia/shared-types'

/**
 * AP-35 — **Anzeige des Lebenslaufs und der Typen** (Detailblatt 1 3.1, 2.6).
 *
 * Eine Stelle für Liste, Detailkopf, Dialoge und das Dashboard. Die Farben sind
 * die Farbrollen des Themes, keine freien Werte (`.claude/rules/styling.md`).
 *
 * Der Typ trägt den Begriff des Mandanten und ist deshalb eine **Funktion**:
 * Eine Konstante könnte den Modus nicht kennen (`.claude/rules/terminologie.md`).
 */
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, { label: string; class: string }> = {
  PREPARATION: { label: 'In Vorbereitung', class: 'bg-muted text-muted-foreground' },
  ACTIVE: { label: 'Aktiv', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  COMPLETED: { label: 'Fertiggestellt', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  CANCELLED: { label: 'Storniert', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
}

/**
 * Beschriftung eines Status im **Protokoll** — dort stehen auch die zehn Werte
 * vor dem 18.9.2026 (Anfrage, beauftragt, abgerechnet …). Sie werden gezeigt,
 * nie mehr gesetzt.
 */
export function statusText(status: string | null | undefined): string {
  if (!status) return '—'
  return PROJECT_STATUS_LABELS[status as ProjectStatus]?.label ?? PROJECT_STATUS_TEXT_BIS_2026_09[status] ?? status
}

export const PROJECT_TYPE_LABELS: Record<string, (terms: Terms) => string> = {
  ORDER: (terms) => terms.project,
  // Ganzes Wort statt Zusammensetzung (terminologie.md) — Phase K.
  COLLECTIVE: (terms) => terms.collective,
  INTERNAL: () => 'Intern',
}

/** Abrechnungsschema und -intervall im Klartext (Detailblatt 3 7.1). */
export const BILLING_SCHEME_LABELS: Record<string, string> = {
  TIME_AND_MATERIAL: 'Regie',
  LUMP_SUM: 'Pauschal',
  UNIT_PRICE: 'Einheitspreis',
  // Altwert — seit 18.9.2026 ein Intervall; bleibt nur zum Anzeigen stehen.
  PAYMENT_PLAN: 'Zahlungsplan',
}

export const BILLING_INTERVAL_LABELS: Record<string, string> = {
  ON_COMPLETION: 'nach Fertigstellung',
  PER_POSITION: 'je Position nach Fertigstellung',
  PAYMENT_PLAN: 'nach Zahlungsplan',
  ADVANCE: 'Vorauskasse',
  DAILY: 'täglich',
  WEEKLY: 'wöchentlich',
  BIWEEKLY: 'zweiwöchentlich',
  TRIWEEKLY: 'dreiwöchentlich',
  MONTHLY: 'monatlich',
  BIMONTHLY: 'zweimonatlich',
  QUARTERLY: 'quartalsmäßig',
  HALF_YEARLY: 'halbjährlich',
  YEARLY: 'jährlich',
  ANY: 'beliebig',
}

/** Woher ein Projekt kommt (Detailblatt 1 2.5). */
export const PROJECT_ORIGIN_LABELS: Record<string, string> = {
  MANUAL: 'von Hand',
  DOCUMENT: 'aus Beleg',
  CALCULATION: 'aus Kalkulation',
  LV_IMPORT: 'aus LV-Import',
  COPY: 'Kopie',
  TIME_TRACKING: 'aus der Zeiterfassung',
}

/** Wie das Protokoll seine Zeilen beschriftet (Detailblatt 1 3.5). */
export const APPROVAL_KIND_LABELS: Record<string, string> = {
  STATUS_CHANGE: 'Statuswechsel',
  HEAD_CHANGE: 'Kopf geändert',
  EXECUTION: 'Ausführungsfreigabe',
  WORKSTEP_RELEASE: 'Freigabe Arbeitsschritt',
  BILLING_DONE: 'Abrechnung fertig',
  DECLARATION_ISSUED: 'Leistungserklärung',
  SUPPLEMENT_APPROVED: 'Nachtrag freigegeben',
  INQUIRY: 'Anfrage freigegeben',
  ORDER: 'Bestellung freigegeben',
  ACCEPTANCE: 'Abnahme',
}

/** Wer einen Eintrag ohne Person ausgelöst hat (`ProjectApproval.trigger`) — nie den Schlüssel zeigen. */
export const APPROVAL_TRIGGER_LABELS: Record<string, string> = {
  CREATED: 'beim Anlegen',
  COPIED: 'beim Kopieren',
  DERIVED: 'aus den Positionen',
  BOM_DEFAULT: 'Vorgabe der Stückliste',
  WORK_ORDER_PRINT: 'beim Drucken',
  WORK_ORDER_ASSIGN: 'beim Zuteilen',
  SITE_DOCS: 'aus den Baustellendokumenten',
}

/** Text zum Auslöser; ein unbekannter Schlüssel wird „automatisch", nicht roh angezeigt. */
export function ausloeserText(trigger?: string | null): string | null {
  if (!trigger) return null
  return APPROVAL_TRIGGER_LABELS[trigger] ?? 'automatisch'
}

/** Ein Datum, wie es im ERP steht — Tag.Monat.Jahr, ohne Uhrzeit. */
export function alsTag(wert?: string | null): string {
  if (!wert) return '—'
  const datum = new Date(wert)
  if (Number.isNaN(datum.getTime())) return '—'
  return datum.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Ein Zeitpunkt für das Protokoll — Tag und Uhrzeit. */
export function alsZeitpunkt(wert?: string | null): string {
  if (!wert) return '—'
  const datum = new Date(wert)
  if (Number.isNaN(datum.getTime())) return '—'
  return datum.toLocaleString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Überfällig: Termin vor heute und noch nicht fertiggestellt (Blatt 1 5.7). */
export function istUeberfaellig(projekt: { completionDueAt?: string | null; status: string }): boolean {
  if (!projekt.completionDueAt) return false
  if (['COMPLETED', 'CANCELLED'].includes(projekt.status)) return false
  return new Date(projekt.completionDueAt) < new Date(new Date().toDateString())
}

// ── Phase D: Position, Arbeitsschritt, Notiz, Ampel ─────────────────────────

/**
 * Lebenslauf der Position (Detailblatt 2 4.1) — seit Nachtrag 7 fünf Stufen,
 * beschriftet und gefärbt wie der Status des Projekts.
 */
export const POSITION_STATUS_LABELS: Record<string, { label: string; class: string }> = {
  PREPARATION: { label: PROJECT_POSITION_STATUS_TEXT.PREPARATION, class: 'bg-muted text-muted-foreground' },
  RELEASED: { label: PROJECT_POSITION_STATUS_TEXT.RELEASED, class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  BLOCKED: { label: PROJECT_POSITION_STATUS_TEXT.BLOCKED, class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  COMPLETED: { label: PROJECT_POSITION_STATUS_TEXT.COMPLETED, class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  CANCELLED: { label: PROJECT_POSITION_STATUS_TEXT.CANCELLED, class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
}

/** Beschriftung eines Positionsstatus im **Protokoll** — dort stehen auch die Werte vor Nachtrag 7. */
export function positionStatusText(status: string | null | undefined): string {
  if (!status) return '—'
  return POSITION_STATUS_LABELS[status]?.label ?? PROJECT_POSITION_STATUS_TEXT_BIS_2026_09[status] ?? status
}

/**
 * Lebenslauf des Arbeitsschritts (Detailblatt 4 4.).
 *
 * `PLANNED` heißt hier **„nicht freigegeben"**, nicht „geplant": Der Schritt
 * steht mit Plänen im System und wartet auf eine Freigabe. Das Wort ist der
 * ganze Unterschied zwischen „noch nicht angefangen" und „darf noch nicht".
 */
export const WORK_STEP_STATUS_LABELS: Record<string, { label: string; class: string }> = {
  PLANNED: { label: 'nicht freigegeben', class: 'bg-muted text-muted-foreground' },
  READY: { label: 'freigegeben', class: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200' },
  ASSIGNED: { label: 'zugeteilt', class: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' },
  IN_PROGRESS: { label: 'in Arbeit', class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  PAUSED: { label: 'pausiert', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  DONE: { label: 'fertig', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  CANCELLED: { label: 'storniert', class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
}

/** Fünf feste Post-it-Farben (Blatt 9 2.2) — keine freie Farbwahl, Theme-Regel. */
export const NOTE_COLOR_CLASSES: Record<string, string> = {
  YELLOW: 'bg-yellow-100 border-yellow-300 dark:bg-yellow-900/40 dark:border-yellow-700',
  BLUE: 'bg-sky-100 border-sky-300 dark:bg-sky-900/40 dark:border-sky-700',
  GREEN: 'bg-green-100 border-green-300 dark:bg-green-900/40 dark:border-green-700',
  PINK: 'bg-pink-100 border-pink-300 dark:bg-pink-900/40 dark:border-pink-700',
  GREY: 'bg-muted border-border',
}

export const NOTE_COLOR_LABELS: Record<string, string> = {
  YELLOW: 'Gelb',
  BLUE: 'Blau',
  GREEN: 'Grün',
  PINK: 'Rosa',
  GREY: 'Grau',
}

/** Die drei Ampeln (Blatt 8 4.). Grau heißt „keine Quelle", nicht „in Ordnung". */
export const TRAFFIC_LIGHT_CLASSES: Record<string, string> = {
  GREEN: 'bg-green-500',
  YELLOW: 'bg-yellow-500',
  RED: 'bg-red-500',
  GREY: 'bg-muted-foreground/40',
}

/** Warum eine Ampel so steht, wie sie steht — der Text zum Schlüssel des Servers. */
export const TRAFFIC_LIGHT_REASONS: Record<string, string> = {
  'projects.trafficLights.scheduleOk': 'Termine liegen im Plan',
  'projects.trafficLights.scheduleOverdue': 'Fertigstellung überschritten',
  'projects.trafficLights.scheduleAssemblyMissed': 'fixierte Montage überschritten',
  'projects.trafficLights.schedulePhaseOverdue': 'eine Phase ist überfällig',
  'projects.trafficLights.schedulePhaseAfterDue': 'Phasenende liegt nach der Fertigstellung',
  'projects.trafficLights.scheduleWithinBuffer': 'Fertigstellung rückt heran',
  'projects.trafficLights.scheduleNoDates': 'keine Termine hinterlegt',
  'projects.trafficLights.costNoModule': 'Controlling ist nicht gebucht',
  'projects.trafficLights.costNoData': 'noch keine Ist-Kosten',
  'projects.trafficLights.costNoPriceRight': 'Beträge nur mit Preisrecht',
  'projects.trafficLights.costNoPlan': 'keine Projektdaten (Soll-Kosten 0)',
  'projects.trafficLights.costTooEarly': 'noch keine Aussage — unter 10 % Leistungsstand',
  'projects.trafficLights.costOk': 'Kosten im Rahmen des Leistungsstands',
  'projects.trafficLights.costWarn': 'Kosten laufen dem Leistungsstand voraus',
  'projects.trafficLights.costOver': 'Kosten deutlich über dem Leistungsstand',
  'projects.trafficLights.progressOk': 'Leistungsstand passt zur verbrauchten Zeit',
  'projects.trafficLights.progressBehind': 'Leistungsstand hinter der Zeit',
  'projects.trafficLights.progressFarBehind': 'Leistungsstand deutlich hinter der Zeit',
  'projects.trafficLights.progressOverdue': 'Termin überschritten, noch nicht fertig',
  'projects.trafficLights.progressNoDates': 'keine Termine hinterlegt',
}

/** Woher der Leistungsstand kommt (Blatt 2 6.). */
export const PROGRESS_SOURCE_LABELS: Record<string, string> = {
  MANUAL: 'Handwert',
  WORK_STEPS: 'aus Arbeitsschritten',
  STATUS: 'aus dem Status',
  NONE: 'geschätzt',
}

/** Art einer Datei (Blatt 6 4.1). */
export const FILE_KIND_LABELS: Record<string, string> = {
  DRAWING: 'Zeichnung',
  PLAN: 'Plan',
  PHOTO: 'Foto',
  OFFER: 'Lieferantenangebot',
  CORRESPONDENCE: 'Schriftwechsel',
  ORDER: 'Auftragsdokument',
  OTHER: 'Sonstiges',
}

/** Woran eine Datei oder ein Verweis hängt (AP-35 4.15). */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  PROJECT: 'Projekt',
  POSITION: 'Position',
  WORK_STEP: 'Arbeitsschritt',
  BOM_LINE: 'Stücklistenzeile',
  DECLARATION: 'Leistungserklärung',
  JOURNAL: 'Journal',
  CERTIFICATE: 'Zeugnis',
  IMPORT_BATCH: 'Import',
}

/** Was aus einem Ablage-Eintrag wird — die Spalte „wird zu" (Blatt 2 7.4). */
export const PASTE_OUTCOME_LABELS: Record<string, string> = {
  POSITION: 'Position',
  GROUP: 'Gruppe',
  LONG_TEXT: 'Langtext',
  SKIPPED: 'nicht übernommen',
}

export const PASTE_NOTE_LABELS: Record<string, string> = {
  'projects.paste.titleSkipped': 'Titelzeile nicht übernommen',
  'projects.paste.appendedToLongText': 'an den Langtext der Position angehängt',
  'projects.paste.appendedToGroup': 'in die Gruppenbezeichnung übernommen',
  'projects.paste.textDropped': 'Textzeile ohne Position verworfen',
  'projects.paste.deductionSkipped': 'Nachlass nicht übernommen — im Beleg abbilden',
  'projects.paste.lineTypeSkipped': 'Zeilenart im Projekt ohne Entsprechung',
  'projects.paste.optionalSkipped': 'Eventual-/Alternativposition nicht übernommen',
  'projects.paste.optionalAsPosition': 'als normale Position übernommen',
  POSITION_DOCUMENT_LINE_LINKED: 'Zeile gehört schon zu einer Position — wird als Kopie angelegt',
  POSITION_DRAFT_UNRESOLVED: 'Einheit unbekannt, Vorgabe gesetzt',
  POSITION_ARTICLE_NOT_SALES: 'Artikel ohne Verkaufsrolle — Verweis entfällt',
}

/** Stunden mit einer Nachkommastelle, wie im Betrieb gesprochen wird. */
export function alsStunden(wert: number | string | null | undefined): string {
  const zahl = typeof wert === 'string' ? Number(wert) : wert
  if (zahl == null || Number.isNaN(zahl)) return '—'
  return `${zahl.toLocaleString('de-AT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} h`
}

/** Eine Dateigröße, wie sie ein Mensch liest. */
export function alsGroesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

/**
 * Die Kennzeile einer Position: „1 PA Treppe mit Podest" (Blatt 2 8.2).
 *
 * Menge, Einheit und Bezeichnung sind **ein** Ausdruck, nicht drei Spalten —
 * so wird eine Position im Betrieb genannt (Auftraggeber 16.9.2026).
 */
export function kennzeile(position: KennzeilenPosition): string {
  const { menge, bezeichnung } = kennzeileTeile(position)
  return [menge, bezeichnung].filter(Boolean).join(' ')
}

type KennzeilenPosition = {
  quantity: string | number
  unit?: { code: string } | null
  name: string
}

/** Die Kennzeile getrennt in „Menge Einheit" und Bezeichnung — für die Darstellung mit fetter Bezeichnung. */
export function kennzeileTeile(position: KennzeilenPosition): { menge: string; bezeichnung: string } {
  const menge = Number(position.quantity)
  const zahl = Number.isNaN(menge)
    ? String(position.quantity)
    : menge.toLocaleString('de-AT', { maximumFractionDigits: 3 })
  return { menge: [zahl, position.unit?.code].filter(Boolean).join(' '), bezeichnung: position.name }
}
